import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUnread } from "@/context/UnreadContext";
import usePendingApprovals from "@/lib/usePendingApprovals";
import AnalogyLogo from "@/components/AnalogyLogo";
import QuickNotesWidget from "@/components/QuickNotesWidget";
import CheckInReminder from "@/components/CheckInReminder";
import CheckOutReminder from "@/components/CheckOutReminder";
import CommandPalette from "@/components/CommandPalette";
import SourceExfilGuard from "@/components/SourceExfilGuard";
import usePermissions from "@/lib/usePermissions";
import { useNotifications } from "@/context/NotificationsContext";
import {
  LayoutDashboard, FolderKanban, HeartHandshake, Building2, MessageSquare, LogOut, User, Search, BookOpen, KeyRound, Sparkles, Bell, Pin, PinOff
} from "lucide-react";

const HUBS = [
  { to: "/", label: "Home", icon: LayoutDashboard, exact: true },
  { to: "/my-work", label: "Projects", icon: FolderKanban },
  { to: "/my-hr", label: "People", icon: HeartHandshake },
  { to: "/crm", label: "CRM", icon: Sparkles, feature: "crm_access" },
  { to: "/studio", label: "Studio", icon: Building2, anyFeature: ["studio_view", "finance", "studio_taxonomy", "audit_log"] },
  { to: "/admin/registry", label: "Registry", icon: BookOpen, anyFeature: ["registry_view", "branding_admin"] },
  { to: "/vault", label: "Vault", icon: KeyRound, feature: "vault_view" },
];

const SIDEBAR_COLLAPSED_W = 72;   // px — icon-only rail width
const SIDEBAR_EXPANDED_W = 256;   // px — w-64

function isHubActive(to, pathname, exact) {
  if (exact) return pathname === to;
  return pathname === to || pathname.startsWith(to + "/") || pathname.startsWith(to + "?");
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { total } = useUnread();
  const { unread: notifUnread } = useNotifications();
  const pendingApprovals = usePendingApprovals(user);
  const can = usePermissions();
  const nav = useNavigate();
  const location = useLocation();

  // Phase 151 — Minimal Sidebar: icon-only by default, expands on hover/click
  // and floats OVER the content (unpinned); Pinning keeps it fully expanded
  // and pushes the content instead, persisted across sessions.
  const [pinned, setPinned] = useState(() => {
    try { return window.localStorage.getItem("sidebar-pinned") === "1"; } catch { return false; }
  });
  const [hovering, setHovering] = useState(false);
  const expanded = pinned || hovering;
  useEffect(() => {
    try { window.localStorage.setItem("sidebar-pinned", pinned ? "1" : "0"); } catch { /* ignore */ }
  }, [pinned]);

  if (!user) return null;
  // Phase 84.44 — Founder-only Access Management overhaul. Nav visibility now
  // purely follows the resolved `user.permissions` map (founder = everyone
  // true; everyone else off until a Founder grants it) — no more admin/adminMode
  // bypass here, since admins are locked down like any other role by default.
  const items = HUBS.filter((n) => {
    if (n.feature) return can(n.feature);
    if (n.anyFeature) return n.anyFeature.some((f) => can(f));
    return true; // Dashboard / Projects / People — their own tabs self-gate
  });
  const showChat = can("chat_view");

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      <CheckInReminder />
      <CheckOutReminder />
      <SourceExfilGuard />

      {/* Mobile top bar — logo + Chat icon */}
      <div
        className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-[#0F172A] text-white flex items-center justify-between px-4 border-b border-white/10"
        data-testid="mobile-topbar"
      >
        <AnalogyLogo size={22} className="text-white" />
        <div className="flex items-center gap-1">
          <NotificationIconButton total={notifUnread} />
          {showChat && <ChatIconButton total={total} />}
          <button onClick={doLogout} className="p-2 text-slate-300 hover:text-white" aria-label="Sign out" data-testid="mobile-logout">
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Desktop left sidebar — Phase 151 Minimal Sidebar (collapsible). When
          pinned, it's a normal flex sibling (pushes content); when unpinned
          it's fixed/floating so hover-expansion overlays the content instead
          of resizing the page underneath it. */}
      <aside
        data-testid="app-sidebar"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onClick={() => !pinned && setHovering(true)}
        style={{ width: expanded ? SIDEBAR_EXPANDED_W : SIDEBAR_COLLAPSED_W }}
        className={`hidden lg:flex bg-[#0F172A] text-slate-200 flex-col h-screen z-40 transition-[width] duration-200 ease-in-out ${
          pinned ? "sticky top-0 shrink-0" : "fixed top-0 left-0 shadow-[8px_0_24px_rgba(0,0,0,0.25)]"
        }`}
      >
        <div className="px-[15px] py-6 border-b border-white/10 shrink-0 flex items-center justify-between min-h-[76px]">
          {expanded ? (
            <AnalogyLogo size={26} className="text-white" />
          ) : (
            <div className="w-9 h-9 rounded-sm bg-[#F54800] flex items-center justify-center font-display font-bold text-white text-sm shrink-0" data-testid="sidebar-mark">
              A
            </div>
          )}
          {expanded && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPinned((p) => !p); }}
              title={pinned ? "Unpin sidebar (float + auto-collapse)" : "Pin sidebar open"}
              data-testid="sidebar-pin-toggle"
              className={`p-1.5 rounded-sm transition-colors shrink-0 ${
                pinned ? "text-[#F54800] bg-[#F54800]/10" : "text-slate-500 hover:text-white hover:bg-white/5"
              }`}
            >
              {pinned ? <PinOff size={15} /> : <Pin size={15} />}
            </button>
          )}
        </div>
        <div className={`overflow-hidden transition-all duration-200 shrink-0 ${expanded ? "max-h-8 opacity-100" : "max-h-0 opacity-0"}`}>
          <div className="label-overline text-slate-400 px-[15px] pt-2">Studio OS</div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-1">
          {items.map(({ to, label, icon: Icon, exact }) => {
            const active = isHubActive(to, location.pathname, exact);
            const showApprovalsBadge = to === "/studio" && pendingApprovals > 0;
            return (
              <SidebarNavRow
                key={to}
                to={to}
                end={exact}
                label={label}
                Icon={Icon}
                active={active}
                expanded={expanded}
                badge={showApprovalsBadge ? pendingApprovals : 0}
                testId={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              />
            );
          })}
          <div className="pt-4 mt-4 border-t border-white/10 space-y-1">
            <SidebarNavRow to="/notifications" label="Notifications" Icon={Bell} expanded={expanded} badge={notifUnread} testId="nav-notifications" />
            {showChat && <SidebarNavRow to="/chat" label="Chat" Icon={MessageSquare} expanded={expanded} badge={total} testId="nav-chat" />}
          </div>
        </nav>
        <div className="px-3 py-4 border-t border-white/10 shrink-0">
          <NavLink
            to="/my-hr?tab=profile"
            data-testid="sidebar-profile"
            title={!expanded ? user.name : undefined}
            className={`flex items-center mb-3 hover:bg-white/5 rounded-sm p-1 -m-1 transition-colors ${expanded ? "gap-3" : "justify-center"}`}
          >
            <div className="w-9 h-9 rounded-sm bg-white/10 flex items-center justify-center overflow-hidden shrink-0">
              {user.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" /> : <User size={16} className="text-white" />}
            </div>
            <div className={`min-w-0 overflow-hidden transition-all duration-200 ${expanded ? "max-w-[160px] opacity-100" : "max-w-0 opacity-0"}`}>
              <div className="text-sm font-medium text-white truncate whitespace-nowrap">{user.name}</div>
              <div className="text-xs text-slate-400 truncate whitespace-nowrap capitalize">{user.role.replace(/_/g, " ")} {user.business_unit ? `· ${user.business_unit}` : ""}</div>
            </div>
          </NavLink>
          <button
            data-testid="logout-btn"
            title={!expanded ? "Sign out" : undefined}
            onClick={doLogout}
            className="w-full text-xs text-slate-200 hover:text-white inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-white/15 hover:bg-white/10 transition-colors min-h-[44px]"
          >
            <LogOut size={14} className="shrink-0" />
            <span className={`overflow-hidden transition-all duration-200 whitespace-nowrap ${expanded ? "max-w-[100px] opacity-100" : "max-w-0 opacity-0"}`}>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Phase 151 — Responsive Centering: when unpinned, the floating rail
          still reserves its collapsed width so content never sits under it;
          when pinned, the aside above is a real flex sibling and needs no
          extra margin. Either way `main`'s own max-w-* + mx-auto containers
          (used throughout the app) recenter automatically as this changes. */}
      <main
        className="flex-1 min-w-0 max-w-full overflow-x-clip pt-14 pb-16 lg:pt-0 lg:pb-0 transition-[margin-left] duration-200 ease-in-out lg:ml-[var(--sbw)]"
        style={{ "--sbw": !pinned ? `${SIDEBAR_COLLAPSED_W}px` : "0px" }}
      >
        {/* Desktop top header — global "+ Create" and Cmd-K trigger */}
        <div className="hidden lg:flex sticky top-0 z-20 bg-white/85 backdrop-blur-sm border-b border-slate-100 h-12 items-center justify-end px-6 gap-2" data-testid="topbar">
          <AdminModePill />
          <NavLink to="/notifications" data-testid="topbar-notifications-desktop" className="relative p-1.5 text-slate-500 hover:text-slate-900" aria-label="Open notifications">
            <Bell size={17} />
            {notifUnread > 0 && (
              <span className="absolute top-0 right-0 bg-[#F54800] text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] px-1 flex items-center justify-center">
                {notifUnread > 99 ? "99+" : notifUnread}
              </span>
            )}
          </NavLink>
          <CmdKTrigger />
        </div>
        <Outlet />
      </main>

      <CommandPalette />

      {/* Mobile bottom tab bar — 4 hubs */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0F172A] border-t border-white/10 grid"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
        data-testid="mobile-bottombar"
      >
        {items.map(({ to, label, icon: Icon, exact }) => {
          const active = isHubActive(to, location.pathname, exact);
          return (
            <NavLink
              key={to}
              to={to}
              end={exact}
              data-testid={`bottombar-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] uppercase tracking-wider transition-colors ${
                active ? "text-[#F54800]" : "text-slate-400"
              }`}
            >
              <Icon size={20} strokeWidth={1.85} />
              <span>{label}</span>
              {active && <span className="absolute top-0 left-1/4 right-1/4 h-[2px] bg-[#F54800]" />}
            </NavLink>
          );
        })}
      </nav>

      <QuickNotesWidget />
    </div>
  );
}

/** Phase 151 — shared collapsible sidebar row: fixed-position icon anchor
 * (px-[15px] stays constant) so only the trailing label/badge animate in
 * width+opacity as the rail expands — the icon never jumps or re-centers. */
function SidebarNavRow({ to, end, label, Icon, active, expanded, badge, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      title={!expanded ? label : undefined}
      data-testid={testId}
      className={({ isActive }) => {
        const isOn = active !== undefined ? active : isActive;
        return `flex items-center gap-3 px-[15px] py-2.5 rounded-sm text-sm transition-colors ${
          isOn ? "bg-[#F54800] text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
        }`;
      }}
    >
      <span className="relative shrink-0 inline-flex">
        <Icon size={17} strokeWidth={1.75} />
        {!expanded && badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-full bg-[#F54800] ring-2 ring-[#0F172A]" data-testid={`${testId}-dot`} />
        )}
      </span>
      <span className={`flex-1 overflow-hidden whitespace-nowrap transition-all duration-200 ${expanded ? "max-w-[140px] opacity-100" : "max-w-0 opacity-0"}`}>
        {label}
      </span>
      {expanded && badge > 0 && (
        <span
          className="bg-[#F54800] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shrink-0"
          data-testid={`${testId}-badge`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

function NotificationIconButton({ total }) {
  return (
    <NavLink
      to="/notifications"
      data-testid="topbar-notifications"
      className="relative p-2 text-slate-200 hover:text-white"
      aria-label="Open notifications"
    >
      <Bell size={20} />
      {total > 0 && (
        <span
          className="absolute top-1 right-1 bg-[#F54800] text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center"
          data-testid="topbar-notifications-badge"
        >
          {total > 99 ? "99+" : total}
        </span>
      )}
    </NavLink>
  );
}

function CmdKTrigger() {
  const fire = () => {
    const evt = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
    window.dispatchEvent(evt);
  };
  return (
    <button
      onClick={fire}
      className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-sm pl-2.5 pr-1.5 py-1.5 transition-colors"
      data-testid="topbar-cmdk"
    >
      <Search size={12} />
      <span className="hidden md:inline">Search…</span>
      <kbd className="text-[10px] font-mono bg-white text-slate-500 px-1 py-px rounded-sm border border-slate-200">⌘K</kbd>
    </button>
  );
}

/* Admin Mode pill — visible only when role=admin (Phase 84.44: founders are
   structurally exempt from every toggle, so this pill is meaningless for
   them now and hidden). Governs a handful of admin-only ACTION buttons
   elsewhere in the app — page-level navigation is governed separately by
   Access Management, not by this toggle. State is stored in localStorage
   via AuthContext. */
function AdminModePill() {
  const { user, adminMode, setAdminMode } = useAuth();
  if (user?.role !== "admin") return null;
  return (
    <button
      onClick={() => setAdminMode(!adminMode)}
      data-testid="admin-mode-toggle"
      title={adminMode ? "Toggles a few admin action buttons — page access is managed by a Founder via Access Management" : "Click to restore admin action buttons"}
      className={`inline-flex items-center gap-2 text-[11px] font-medium rounded-sm pl-2.5 pr-3 py-1.5 transition-colors ${
        adminMode
          ? "bg-[#F54800]/10 text-[#B7350A] hover:bg-[#F54800]/15 border border-[#F54800]/25"
          : "bg-slate-100 text-slate-500 hover:bg-slate-200 border border-slate-200"
      }`}
    >
      <span
        className={`w-2 h-2 rounded-full ${adminMode ? "bg-[#F54800] shadow-[0_0_0_2px_rgba(245,72,0,0.18)]" : "bg-slate-400"}`}
      />
      Admin mode <b className="font-bold ml-0.5" data-testid="admin-mode-state">{adminMode ? "ON" : "OFF"}</b>
    </button>
  );
}


function ChatIconButton({ total }) {
  return (
    <NavLink
      to="/chat"
      data-testid="topbar-chat"
      className="relative p-2 text-slate-200 hover:text-white"
      aria-label="Open chat"
    >
      <MessageSquare size={20} />
      {total > 0 && (
        <span
          className="absolute top-1 right-1 bg-[#F54800] text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center"
          data-testid="topbar-unread-badge"
        >
          {total > 99 ? "99+" : total}
        </span>
      )}
    </NavLink>
  );
}

// PageHeader / StatCard / Tag re-exports unchanged
export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
      <div className="min-w-0">
        {eyebrow && <div className="label-overline mb-2 text-[#F54800]">{eyebrow}</div>}
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">{title}</h1>
        {subtitle && <p className="text-slate-600 mt-2 max-w-2xl text-sm sm:text-base">{subtitle}</p>}
      </div>
      {actions && <div className="shrink-0 flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, accent, onClick, className = "", ...rest }) {
  const isClickable = typeof onClick === "function";
  const Tag = isClickable ? "button" : "div";
  return (
    <Tag
      type={isClickable ? "button" : undefined}
      onClick={onClick}
      className={`card-flat p-4 sm:p-5 text-left w-full flex flex-col ${isClickable ? "hover:border-slate-300 hover:-translate-y-0.5 transition cursor-pointer" : ""} ${className}`}
      {...rest}
    >
      <div className="label-overline">{label}</div>
      <div className={`mt-2 font-display text-2xl sm:text-3xl font-bold ${accent || "text-slate-900"}`}>{value}</div>
      {/* Phase 97 Batch 2 — always reserve the hint line so every StatCard
          is the same height whether or not it has a hint (uniform box sizing). */}
      <div className={`text-xs mt-2 min-h-[16px] ${accent || "text-slate-500"}`}>{hint || "\u00A0"}</div>
    </Tag>
  );
}

export function Tag({ children, tone = "slate", className = "", ...rest }) {
  const map = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-green-100 text-green-800",
    orange: "bg-orange-100 text-orange-800",
    red: "bg-red-100 text-red-800",
    yellow: "bg-yellow-100 text-yellow-800",
    blue: "bg-blue-100 text-blue-800",
    purple: "bg-purple-100 text-purple-800",
    brand: "bg-[#F54800]/10 text-[#F54800]",
  };
  return (
    <span
      {...rest}
      className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-sm tracking-wide ${map[tone]} ${className}`.trim()}
    >
      {children}
    </span>
  );
}
